import { IClientInstallation } from '@api/models';
import FormikDatePicker from '@components/FormFields/FormikDatePicker';
import RequiredMark from '@components/FormFields/RequiredMark';
import useDialog from '@hooks/use-dialog';
import { Button, Card, CircularProgress, HelpText, Label } from '@hyperflake/react-ui-library';
import { DeleteIcon, LockIcon, PlusIcon, UnlockIcon } from '@icon/index';
import { FormHelperText } from '@library/forms';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorMessage, FieldArray, Form, Formik } from 'formik';
import { useBusinessDevelopmentListData } from 'modules/client-installation/hooks/useBusinessDevelopmentListData';
import useClientInstallationUpdateForm from 'modules/client-installation/hooks/useClientInstallationUpdateForm';
import { FormikInput, FormikSelect } from 'modules/shared/features/formik';
import { useMemo, useState } from 'react';
import DeleteClientInstallationDrawer from '../DeleteClientInstallationDrawer/DeleteClientInstallationDrawer';
import { toast } from 'react-toastify';
import FileUploadClientInstallation from '../FileUploadClientInstallation/FileUploadClientInstallation';
import { countryOptions } from 'modules/shared/helpers/shared.constants';
import { ClientInstallationStatusEnum } from '@api/enum/client-installation.enum';

interface IUpdateClientInstallationFormProps {
    initialData: IClientInstallation;
    setData: (data: any) => void;
}

const UpdateClientInstallationForm = (props: IUpdateClientInstallationFormProps) => {
    const { initialData, setData } = props;
    const formHook = useClientInstallationUpdateForm(initialData);
    const { businessDevelopmentUser, isLoading } = useBusinessDevelopmentListData();
    const queryClient = useQueryClient();
    const deleteClientInstallationDialog = useDialog<IClientInstallation>();
    const [enableEdit, setEnableEdit] = useState<boolean>(false);

    const BdUserOption = useMemo(() => {
        if (!businessDevelopmentUser) return [];
        return [
            { label: 'Select BD', value: '' },
            ...businessDevelopmentUser.map((val) => ({ label: val.name, value: val._id })),
        ];
    }, [businessDevelopmentUser]);

    const handleSubmit = async (values: any) => {
        try {
            const data = await formHook.submit(values);
            if (data) {
                await queryClient.invalidateQueries(['client-installation', data._id]);
            }
            setEnableEdit(false);
            toast.success('Client Updated Successfully');
        } catch (err) {
            console.error('Error submitting form:', err);
        }
    };
    const statusOptions = useMemo(
        () => [
            { label: 'Complete', value: ClientInstallationStatusEnum.COMPLETE },
            { label: 'On-Hold', value: ClientInstallationStatusEnum.ON_HOLD },
            { label: 'Pending', value: ClientInstallationStatusEnum.PENDING },
        ],
        []
    );

    return (
        <>
            <Formik
                initialValues={formHook.initialValues}
                validationSchema={formHook.schema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, setFieldValue }) => (
                    <Form>
                        {/* Basic Info */}
                        <Card className="mt-4">
                            <Card.Header className="flex justify-between items-center">
                                <div>Basic Information</div>
                                <div>
                                    {enableEdit ? (
                                        <div className="flex gap-4">
                                            <Button type="button" variant="shaded" onClick={() => setEnableEdit(false)}>
                                                Cancel
                                            </Button>
                                            <Button disabled={formHook.isSubmitting || !enableEdit}>
                                                {formHook.isSubmitting ? 'Updating...' : 'Save'}
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button type="button" variant="shaded" onClick={() => setEnableEdit(true)}>
                                            <LockIcon width={18} height={22} className="text-grayscale-600" />
                                            Edit
                                        </Button>
                                    )}
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <fieldset disabled={!enableEdit}>
                                    <Label>
                                        Name <RequiredMark />
                                    </Label>
                                    <FormikInput name="name" type="text" />
                                    <HelpText variant="error">
                                        <ErrorMessage name="name" />
                                    </HelpText>

                                    <div className="mt-4">
                                        <Label>
                                            Business Development <RequiredMark />
                                        </Label>
                                        <div className="flex gap-2">
                                            <FormikSelect name="businessDevelopmentUser" options={BdUserOption} />
                                            {isLoading && <CircularProgress size={14} />}
                                        </div>
                                        <HelpText variant="error">
                                            <ErrorMessage name="businessDevelopmentUser" />
                                        </HelpText>
                                    </div>
                                    <div className="mt-4">
                                        <Label>
                                            Status <RequiredMark />
                                        </Label>
                                        <FormikSelect name="status" options={statusOptions} />
                                        <HelpText variant="error">
                                            <ErrorMessage name="status" />
                                        </HelpText>
                                    </div>
                                </fieldset>
                            </Card.Body>
                        </Card>

                        <fieldset disabled={!enableEdit}>
                            {/* POCs */}
                            <Card className="mt-4">
                                <Card.Header>POCs</Card.Header>
                                <Card.Body>
                                    <FieldArray name="poc">
                                        {({ remove, push }) => (
                                            <>
                                                {values.poc.map((_, index) => (
                                                    <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                                        <div className="md:col-span-3">
                                                            <Label>
                                                                Name <RequiredMark />
                                                            </Label>
                                                            <FormikInput name={`poc.${index}.name`} type="text" />
                                                            <HelpText variant={'error'}>
                                                                <ErrorMessage name={`poc.${index}.name`} />
                                                            </HelpText>
                                                        </div>
                                                        <div className="md:col-span-3">
                                                            <Label>
                                                                Email <RequiredMark />
                                                            </Label>
                                                            <FormikInput name={`poc.${index}.email`} type="email" />
                                                            <HelpText variant={'error'}>
                                                                <ErrorMessage name={`poc.${index}.email`} />
                                                            </HelpText>
                                                        </div>
                                                        <div className="md:col-span-3">
                                                            <Label>Phone</Label>
                                                            <FormikInput
                                                                name={`poc.${index}.phoneNumber`}
                                                                type="number"
                                                            />
                                                            <HelpText variant={'error'}>
                                                                <ErrorMessage name={`poc.${index}.phoneNumber`} />
                                                            </HelpText>
                                                        </div>
                                                        <div className="md:col-span-2">
                                                            <Label>Designation</Label>
                                                            <FormikInput
                                                                name={`poc.${index}.designation`}
                                                                type="text"
                                                            />
                                                        </div>
                                                        <div className="flex items-end md:items-center md:py-6">
                                                            <Button
                                                                type="button"
                                                                size="xs"
                                                                onClick={() => remove(index)}
                                                            >
                                                                <DeleteIcon />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                                <Button
                                                    className="mt-4"
                                                    size="sm"
                                                    type="button"
                                                    onClick={() =>
                                                        push({
                                                            name: '',
                                                            email: '',
                                                            phoneNumber: '',
                                                            designation: '',
                                                        })
                                                    }
                                                >
                                                    <PlusIcon />
                                                </Button>
                                            </>
                                        )}
                                    </FieldArray>
                                </Card.Body>
                            </Card>

                            {/* Location */}
                            <Card className="mt-4">
                                <Card.Header>Location</Card.Header>
                                <Card.Body>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <Label>
                                                Country <RequiredMark />
                                            </Label>
                                            <FormikSelect name="location.country" options={countryOptions} showSearch />
                                            <HelpText variant="error">
                                                <ErrorMessage name="location.country" />
                                            </HelpText>
                                        </div>
                                        <div className="md:col-span-2">
                                            <Label>Google Maps Link</Label>
                                            <FormikInput name="location.googleMapsLink" type="text" />
                                        </div>
                                        <div>
                                            <Label>Latitude</Label>
                                            <FormikInput name="location.latitude" type="text" />
                                        </div>
                                        <div>
                                            <Label>Longitude</Label>
                                            <FormikInput name="location.longitude" type="text" />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* Contract */}
                            <Card className="mt-4">
                                <Card.Header>Contract</Card.Header>
                                <Card.Body>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <Label>Start Date</Label>
                                            <FormikDatePicker name="contract.startDate" isClearable />
                                            <FormHelperText variant="error">
                                                <ErrorMessage name="contract.startDate" />
                                            </FormHelperText>
                                        </div>
                                        <div>
                                            <Label>End Date</Label>
                                            <FormikDatePicker
                                                name="contract.endDate"
                                                isClearable
                                                // minDate={values.contract.startDate?.toDate?.()}
                                            />
                                            <FormHelperText variant="error">
                                                <ErrorMessage name="contract.endDate" />
                                            </FormHelperText>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </fieldset>

                        <Button
                            type="submit"
                            className="mt-6  w-full sm:w-auto"
                            disabled={formHook.isSubmitting || !enableEdit}
                        >
                            {formHook.isSubmitting ? 'Updating...' : 'Update Client'}
                        </Button>
                    </Form>
                )}
            </Formik>

            <div>
                <FileUploadClientInstallation initialData={initialData} setData={setData} enableEdit={enableEdit} />
            </div>

            {enableEdit && (
                <Card className="mt-4">
                    <Card.Header>Client Delete</Card.Header>
                    <Card.Body>
                        <Button onClick={() => deleteClientInstallationDialog.show(initialData)}>Delete</Button>
                    </Card.Body>
                </Card>
            )}

            <DeleteClientInstallationDrawer
                open={deleteClientInstallationDialog.isOpen}
                onClose={deleteClientInstallationDialog.hide}
                clientInstallation={deleteClientInstallationDialog.data}
            />
        </>
    );
};

export default UpdateClientInstallationForm;
